import type { ReactNode } from "react";

import dayjs from "dayjs";

import { useTranslate } from "@similarweb/i18n-provider";
import { Salesloft } from "@similarweb/icons";
import { shortMonthDayAndYear } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Badge, TruncatedTextWithTooltip } from "@similarweb/ui-components";
import { Box, Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import type { SalesloftCadence } from "app/pages/sales-intelligence/modules/salesloft/types/salesloft.types";

import { CadenceFunctionBadge } from "./CadenceFunctionBadge";
import { CadenceStepsSummary } from "./CadenceStepsSummary";

type CadenceCardProps = {
    cadence: SalesloftCadence;
    isSelected: boolean;
    onSelect?: (cadence: SalesloftCadence) => void;
    footer?: ReactNode;
    readOnly?: boolean;
    maxWidth?: string;
};

export const CadenceCard = ({
    cadence,
    isSelected,
    onSelect,
    footer,
    readOnly = false,
    maxWidth,
}: CadenceCardProps) => {
    const { translate } = useTranslate();

    return (
        <Stack
            gap="8px"
            padding="10px 12px"
            borderRadius="6px"
            minWidth="0"
            width="100%"
            maxWidth={maxWidth}
            cursor={readOnly ? undefined : "pointer"}
            background={isSelected ? colorsPalettes.blue["50"] : colorsPalettes.carbon["0"]}
            border={
                isSelected ? `1px solid ${colorsPalettes.blue["400"]}` : `1px solid ${colorsPalettes.midnight["50"]}`
            }
            whenHover={
                readOnly
                    ? undefined
                    : { borderColor: isSelected ? colorsPalettes.blue["400"] : colorsPalettes.blue["200"] }
            }
            onClick={readOnly ? undefined : () => onSelect?.(cadence)}
            data-automation="playbook-cadence-card"
            data-automation-selected={isSelected}
        >
            <Flex
                gap="10px"
                minWidth="0"
                width="100%"
            >
                <Box flex="none">
                    <Salesloft
                        size="lg"
                        color={colorsPalettes.carbon["500"]}
                    />
                </Box>
                <Stack
                    flex="1 1 0"
                    minWidth="0"
                    overflow="hidden"
                >
                    <Flex
                        justifyContent="space-between"
                        alignItems="flex-start"
                        gap="12px"
                        minWidth="0"
                    >
                        <Box
                            minWidth="0"
                            flex="1 1 0"
                            overflow="hidden"
                        >
                            <TruncatedTextWithTooltip
                                fontVariant="body1"
                                color={isSelected ? colorsPalettes.blue["400"] : colorsPalettes.carbon["500"]}
                                width="100%"
                            >
                                {cadence.name ?? ""}
                            </TruncatedTextWithTooltip>
                        </Box>
                        <CadenceStepsSummary steps={cadence.steps} />
                    </Flex>
                    <Flex
                        alignItems="center"
                        gap="8px"
                    >
                        {cadence.cadenceFunction ? (
                            <CadenceFunctionBadge cadenceFunction={cadence.cadenceFunction} />
                        ) : null}
                        <Badge
                            bgColor={cadence.shared ? colorsPalettes.carbon["25"] : colorsPalettes.indigo["100"]}
                            color={cadence.shared ? colorsPalettes.carbon["400"] : colorsPalettes.indigo["400"]}
                            type="custom"
                        >
                            {cadence.shared
                                ? translate("si.playbooks.cadence.type.shared")
                                : translate("si.playbooks.cadence.type.private")}
                        </Badge>
                        {cadence.updatedAt ? (
                            <Text
                                fontVariant="caption"
                                color={colorsPalettes.carbon["300"]}
                            >
                                {translate("si.playbooks.cadence.updated", {
                                    date: dayjs(cadence.updatedAt).format(shortMonthDayAndYear),
                                })}
                            </Text>
                        ) : null}
                    </Flex>
                </Stack>
            </Flex>
            {footer}
        </Stack>
    );
};
